import React, { useCallback, useEffect } from 'react';
import {
  Space,
  Checkbox,
  Tag,
  Select,
  Typography,
  Row,
  Col,
  Input
} from 'antd';
import {
  DeliveryOptions,
  InappWebDeliveryOptions
} from '../../../../interfaces/backend/Notification';
import { HOUR_OPTIONS, MONTHLY_OPTIONS, WEEKLY_OPTIONS } from './labels';
import Link from 'antd/es/typography/Link';
import styled from 'styled-components';
import { Channels } from '../../../common/channelsEnum';

const { Text } = Typography;

const CustomLink = styled(Link)`
  opacity: 0.5;
  &:hover {
    opacity: 1;
  }
`;

export interface ChannelDeliveryOptionsParams {
  channel: Channels;
  deliveryOptions: DeliveryOptions | InappWebDeliveryOptions | undefined;
  onDeliveryOptionsChange: (
    options: DeliveryOptions,
    channel: Channels
  ) => void;
}

const ChannelDeliveryOptions: React.FC<ChannelDeliveryOptionsParams> = ({
  channel,
  deliveryOptions,
  onDeliveryOptionsChange
}) => {
  const instantly = deliveryOptions?.instant?.enabled ?? false;
  const inAppBatching =
    channel === Channels.INAPP_WEB
      ? (deliveryOptions as InappWebDeliveryOptions)?.instant?.batching ?? false
      : undefined;

  const hourly =
    channel === Channels.EMAIL
      ? (deliveryOptions as DeliveryOptions)?.hourly?.enabled ?? false
      : undefined;
  const daily =
    channel === Channels.EMAIL
      ? (deliveryOptions as DeliveryOptions)?.daily?.enabled ?? false
      : undefined;
  const dailyTime =
    channel === Channels.EMAIL
      ? (deliveryOptions as DeliveryOptions)?.daily?.hour ??
        HOUR_OPTIONS[0].value
      : undefined;
  const weekly =
    channel === Channels.EMAIL
      ? (deliveryOptions as DeliveryOptions)?.weekly?.enabled ?? false
      : undefined;
  const weeklyTime =
    channel === Channels.EMAIL
      ? (deliveryOptions as DeliveryOptions)?.weekly?.hour ??
        HOUR_OPTIONS[0].value
      : undefined;
  const weeklyDate =
    channel === Channels.EMAIL
      ? (deliveryOptions as DeliveryOptions)?.weekly?.day ??
        WEEKLY_OPTIONS[0].value
      : undefined;
  const monthly =
    channel === Channels.EMAIL
      ? (deliveryOptions as DeliveryOptions)?.monthly?.enabled ?? false
      : undefined;
  const monthlyTime =
    channel === Channels.EMAIL
      ? (deliveryOptions as DeliveryOptions)?.monthly?.hour ??
        HOUR_OPTIONS[0].value
      : undefined;
  const monthlyDate =
    channel === Channels.EMAIL
      ? (deliveryOptions as DeliveryOptions)?.monthly?.date ??
        WEEKLY_OPTIONS[0].value
      : undefined;
  const allowUnsubscribe = deliveryOptions?.off?.enabled ?? true;
  const defaultOption = deliveryOptions?.defaultDeliveryOption;

  const userOptions = [];

  instantly &&
    userOptions.push({
      label: 'Instantly',
      value: 'instant'
    });
  hourly &&
    userOptions.push({
      label: 'Every hour',
      value: 'hourly'
    });
  daily &&
    userOptions.push({
      label: `Every day at ${
        HOUR_OPTIONS.find((option) => option.value === dailyTime)?.label
      }`,
      value: 'daily'
    });
  weekly &&
    userOptions.push({
      label: `Every ${
        WEEKLY_OPTIONS.find((option) => option.value === weeklyDate)?.label
      } at ${
        HOUR_OPTIONS.find((option) => option.value === weeklyTime)?.label
      }`,
      value: 'weekly'
    });
  monthly &&
    userOptions.push({
      label: `${
        MONTHLY_OPTIONS.find((option) => option.value === monthlyDate)?.label
      } at ${
        HOUR_OPTIONS.find((option) => option.value === monthlyTime)?.label
      }`,
      value: 'monthly'
    });
  allowUnsubscribe &&
    userOptions.push({
      label: 'Off / Unsubscribed',
      value: 'off'
    });

  const updateDefaultOption = useCallback(
    (option: DeliveryOptions['defaultDeliveryOption']) => {
      onDeliveryOptionsChange(
        {
          ...deliveryOptions,
          defaultDeliveryOption: option
        },
        channel
      );
    },
    [channel, deliveryOptions, onDeliveryOptionsChange]
  );

  const updateEmailOptions = (
    option: 'instant' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'off',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updatedOptions: any
  ) => {
    const newOptions: DeliveryOptions = {
      ...deliveryOptions
    };

    newOptions[option] = {
      ...newOptions[option],
      ...updatedOptions
    };

    onDeliveryOptionsChange(newOptions, channel);
  };

  const updateInAppWebOptions = (
    option: 'off' | 'instant',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updatedOptions: any
  ) => {
    const newOptions: InappWebDeliveryOptions = {
      ...(deliveryOptions as InappWebDeliveryOptions)
    };

    newOptions[option] = {
      ...newOptions[option],
      ...updatedOptions
    };

    onDeliveryOptionsChange(newOptions, channel);
  };
  useEffect(() => {
    // update the defaultOption if the defaultOption is not valid
    if (channel === Channels.EMAIL) {
      const currentOption = deliveryOptions?.defaultDeliveryOption;
      let newOption: DeliveryOptions['defaultDeliveryOption'];
      if (
        (currentOption &&
          !(deliveryOptions as DeliveryOptions)?.[currentOption]?.enabled) ||
        (currentOption === undefined &&
          ((deliveryOptions as DeliveryOptions)?.instant?.enabled ||
            (deliveryOptions as DeliveryOptions)?.hourly?.enabled ||
            (deliveryOptions as DeliveryOptions)?.daily?.enabled ||
            (deliveryOptions as DeliveryOptions)?.weekly?.enabled ||
            (deliveryOptions as DeliveryOptions)?.monthly?.enabled ||
            (deliveryOptions as DeliveryOptions)?.off?.enabled))
      ) {
        newOption = deliveryOptions?.instant?.enabled
          ? 'instant'
          : (deliveryOptions as DeliveryOptions)?.hourly?.enabled
          ? 'hourly'
          : (deliveryOptions as DeliveryOptions)?.daily?.enabled
          ? 'daily'
          : (deliveryOptions as DeliveryOptions)?.weekly?.enabled
          ? 'weekly'
          : (deliveryOptions as DeliveryOptions)?.monthly?.enabled
          ? 'monthly'
          : (deliveryOptions as DeliveryOptions)?.off?.enabled
          ? 'off'
          : undefined;

        if (newOption !== currentOption) {
          updateDefaultOption(newOption);
        }
      }
    }
    if (channel === Channels.INAPP_WEB) {
      const currentOption = deliveryOptions?.defaultDeliveryOption as
        | 'off'
        | 'instant';
      let newOption: InappWebDeliveryOptions['defaultDeliveryOption'];
      if (
        (currentOption &&
          !(deliveryOptions as InappWebDeliveryOptions)?.[currentOption]
            ?.enabled) ||
        (currentOption === undefined &&
          ((deliveryOptions as InappWebDeliveryOptions)?.instant?.enabled ||
            (deliveryOptions as InappWebDeliveryOptions)?.off?.enabled))
      ) {
        newOption = deliveryOptions?.instant?.enabled
          ? 'instant'
          : (deliveryOptions as InappWebDeliveryOptions)?.off?.enabled
          ? 'off'
          : undefined;

        if (newOption !== currentOption) {
          updateDefaultOption(newOption);
        }
      }
    }
  }, [channel, deliveryOptions, updateDefaultOption]);

  return (
    <>
      {channel === Channels.EMAIL && (
        <div style={{ width: '100' }}>
          <Space direction="vertical" size="middle">
            <Row style={{ marginBottom: '8px' }}>
              <Text type="secondary">
                Choose what delivery options to support for this notification:
              </Text>
            </Row>

            <Row align="middle">
              <Col style={{ width: 90 }}>
                <Checkbox
                  checked={instantly}
                  onChange={() =>
                    updateEmailOptions('instant', { enabled: !instantly })
                  }
                >
                  Instantly
                </Checkbox>
              </Col>
              <Col>
                {defaultOption === 'instant' ? (
                  <Tag color="green">Default</Tag>
                ) : (
                  instantly && (
                    <CustomLink
                      type="secondary"
                      onClick={() => {
                        updateDefaultOption('instant');
                      }}
                    >
                      set as default
                    </CustomLink>
                  )
                )}
              </Col>
            </Row>

            <Row align="middle">
              <Col style={{ width: 90 }}>
                <Checkbox
                  checked={hourly}
                  onChange={() =>
                    updateEmailOptions('hourly', { enabled: !hourly })
                  }
                >
                  Hourly
                </Checkbox>
              </Col>
              <Col>
                {defaultOption === 'hourly' ? (
                  <Tag color="green">Default</Tag>
                ) : (
                  hourly && (
                    <CustomLink
                      type="secondary"
                      onClick={() => {
                        updateDefaultOption('hourly');
                      }}
                    >
                      set as default
                    </CustomLink>
                  )
                )}
              </Col>
            </Row>

            <Row align="middle">
              <Col style={{ width: 90 }}>
                <Checkbox
                  style={{ width: '100%' }}
                  checked={daily}
                  onChange={() =>
                    updateEmailOptions('daily', { enabled: !daily })
                  }
                >
                  Daily
                </Checkbox>
              </Col>
              <Col>
                <Select
                  disabled={!daily}
                  style={{ width: 120, marginRight: 10 }}
                  onChange={(value) =>
                    updateEmailOptions('daily', { hour: value })
                  }
                  options={HOUR_OPTIONS}
                  value={dailyTime}
                />
              </Col>
              <Col>
                {defaultOption === 'daily' ? (
                  <Tag color="green">Default</Tag>
                ) : (
                  daily && (
                    <CustomLink
                      type="secondary"
                      onClick={() => {
                        updateDefaultOption('daily');
                      }}
                    >
                      set as default
                    </CustomLink>
                  )
                )}
              </Col>
            </Row>

            <Row align="middle">
              <Col style={{ width: 90 }}>
                <Checkbox
                  checked={weekly}
                  onChange={() =>
                    updateEmailOptions('weekly', { enabled: !weekly })
                  }
                >
                  Weekly
                </Checkbox>
              </Col>
              <Col>
                <Select
                  disabled={!weekly}
                  style={{ width: 120, marginRight: 10 }}
                  onChange={(value) =>
                    updateEmailOptions('weekly', { day: value })
                  }
                  options={WEEKLY_OPTIONS}
                  value={weeklyDate}
                ></Select>
              </Col>
              <Col>
                <Select
                  style={{ marginRight: 10 }}
                  disabled={!weekly}
                  onChange={(value) =>
                    updateEmailOptions('weekly', { hour: value })
                  }
                  options={HOUR_OPTIONS}
                  value={weeklyTime}
                />
              </Col>

              <Col>
                {defaultOption === 'weekly' ? (
                  <Tag color="green">Default</Tag>
                ) : (
                  weekly && (
                    <CustomLink
                      type="secondary"
                      onClick={() => {
                        updateDefaultOption('weekly');
                      }}
                    >
                      set as default
                    </CustomLink>
                  )
                )}
              </Col>
            </Row>

            <Row align="middle">
              <Col style={{ width: 90 }}>
                <Checkbox
                  checked={monthly}
                  onChange={() =>
                    updateEmailOptions('monthly', { enabled: !monthly })
                  }
                >
                  Monthly
                </Checkbox>
              </Col>
              <Col>
                <Select
                  disabled={!monthly}
                  style={{ width: 150, marginRight: 10 }}
                  onChange={(value) =>
                    updateEmailOptions('monthly', { date: value })
                  }
                  options={MONTHLY_OPTIONS}
                  value={monthlyDate}
                ></Select>
              </Col>
              <Col>
                <Select
                  disabled={!monthly}
                  style={{ marginRight: 10 }}
                  onChange={(value) =>
                    updateEmailOptions('monthly', { hour: value })
                  }
                  options={HOUR_OPTIONS}
                  value={monthlyTime}
                />
              </Col>
              <Col>
                {defaultOption === 'monthly' ? (
                  <Tag color="green">Default</Tag>
                ) : (
                  monthly && (
                    <CustomLink
                      type="secondary"
                      onClick={() => {
                        updateDefaultOption('monthly');
                      }}
                    >
                      set as default
                    </CustomLink>
                  )
                )}
              </Col>
            </Row>

            <Row>
              <Col>
                <Checkbox
                  checked={allowUnsubscribe}
                  onChange={() =>
                    updateEmailOptions('off', { enabled: !allowUnsubscribe })
                  }
                >
                  Allow Unsubscribing / Off
                </Checkbox>
              </Col>
              <Col>
                {deliveryOptions?.defaultDeliveryOption === 'off' ? (
                  <Tag color="green">Default</Tag>
                ) : (
                  allowUnsubscribe && (
                    <CustomLink
                      type="secondary"
                      onClick={() => {
                        updateDefaultOption('off');
                      }}
                    >
                      set as default
                    </CustomLink>
                  )
                )}
              </Col>
            </Row>
          </Space>
        </div>
      )}
      {channel === Channels.INAPP_WEB && (
        <div style={{ width: '100' }}>
          <Space direction="vertical" size="middle">
            <Row style={{ marginBottom: '8px' }}>
              <Text type="secondary">
                Choose what delivery options to support for this notification:
              </Text>
            </Row>

            <Row align="middle">
              <Col style={{ width: 90 }}>
                <Checkbox
                  checked={instantly}
                  onChange={() =>
                    updateInAppWebOptions('instant', { enabled: !instantly })
                  }
                >
                  Instantly
                </Checkbox>
              </Col>
              <Col>
                {defaultOption === 'instant' ? (
                  <Tag color="green">Default</Tag>
                ) : (
                  instantly && (
                    <CustomLink
                      type="secondary"
                      onClick={() => {
                        updateDefaultOption('instant');
                      }}
                    >
                      set as default
                    </CustomLink>
                  )
                )}
              </Col>
            </Row>
            <Row>
              <Col span={2}></Col>
              <Col>
                <Row>
                  {' '}
                  <Checkbox
                    checked={inAppBatching}
                    onChange={() =>
                      updateInAppWebOptions('instant', {
                        batching: !inAppBatching
                      })
                    }
                  >
                    Batch items as they arrive
                  </Checkbox>
                </Row>
                <Row>
                  <Col style={{ marginTop: '5%', marginLeft: '10%' }}>
                    <Typography.Text>Batch by:</Typography.Text>
                    <Input
                      disabled={!inAppBatching}
                      placeholder="a mergeTag; e.g. commentId"
                      size="small"
                      value={
                        (deliveryOptions as InappWebDeliveryOptions)?.instant
                          ?.batchingKey
                      }
                      onChange={(event) => {
                        updateInAppWebOptions('instant', {
                          batchingKey: event.target.value
                        });
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col style={{ marginTop: '3%', marginLeft: '10%' }}>
                    <Typography.Text>
                      Batching window (seconds):
                    </Typography.Text>
                    <Input
                      placeholder="batch items within this many seconds"
                      disabled={!inAppBatching}
                      size="small"
                      value={
                        (deliveryOptions as InappWebDeliveryOptions)?.instant
                          ?.batchingWindow
                      }
                      onChange={(event) => {
                        updateInAppWebOptions('instant', {
                          batchingWindow: event.target.value
                        });
                      }}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={2}> </Col>
            </Row>
            <Row>
              <Col>
                <Checkbox
                  checked={allowUnsubscribe}
                  onChange={() =>
                    updateInAppWebOptions('off', { enabled: !allowUnsubscribe })
                  }
                >
                  Allow Unsubscribing / Off
                </Checkbox>
              </Col>
              <Col>
                {deliveryOptions?.defaultDeliveryOption === 'off' ? (
                  <Tag color="green">Default</Tag>
                ) : (
                  allowUnsubscribe && (
                    <CustomLink
                      type="secondary"
                      onClick={() => {
                        updateDefaultOption('off');
                      }}
                    >
                      set as default
                    </CustomLink>
                  )
                )}
              </Col>
            </Row>
          </Space>
        </div>
      )}
    </>
  );
};

export default ChannelDeliveryOptions;
